import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "../components/ui/dialog";
import {
  ArrowLeft,
  Bell,
  AlertTriangle,
  Info,
  Send,
  Users,
  History,
  MessageSquare
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBulkAlerts = ({ user }) => {
  const navigate = useNavigate();
  const [alertHistory, setAlertHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  const [alertForm, setAlertForm] = useState({
    title: "",
    message: "",
    urgency: "info",
    target: "all_responders",
    include_sms: false
  });

  useEffect(() => {
    loadAlertHistory();
  }, []);

  const loadAlertHistory = async () => {
    try {
      const response = await axios.get(`${API}/admin/bulk-alerts`);
      setAlertHistory(response.data.alerts);
    } catch (error) {
      console.error("Failed to load alert history:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendAlert = async () => {
    if (!alertForm.title.trim() || !alertForm.message.trim()) {
      toast.error("Please fill in title and message");
      return;
    }

    setIsSending(true);
    try {
      const response = await axios.post(`${API}/admin/bulk-alert`, alertForm);
      toast.success(`Alert sent to ${response.data.stats.total_recipients} responders`);
      setAlertForm({
        title: "",
        message: "",
        urgency: "info",
        target: "all_responders",
        include_sms: false
      });
      setShowConfirmDialog(false);
      loadAlertHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send alert");
    } finally {
      setIsSending(false);
    }
  };

  const getUrgencyIcon = (urgency) => {
    switch (urgency) {
      case "emergency": return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case "warning": return <AlertTriangle className="w-4 h-4 text-amber-600" />;
      default: return <Info className="w-4 h-4 text-blue-600" />;
    }
  };

  const getUrgencyBadge = (urgency) => {
    switch (urgency) {
      case "emergency": return <Badge variant="destructive">Emergency</Badge>;
      case "warning": return <Badge className="bg-amber-500">Warning</Badge>;
      default: return <Badge variant="secondary">Info</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  // Check admin access
  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Access Denied</h2>
            <p className="text-muted-foreground mb-4">
              You need admin privileges to access this page.
            </p>
            <Button onClick={() => navigate("/dashboard")}>
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  Bulk Alerts
                </h1>
                <p className="text-sm text-muted-foreground">
                  Send notifications to all responders
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Create Alert Card */}
        <Card className="card-valor mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              Send Bulk Alert
            </CardTitle>
            <CardDescription>
              Notify all responders about important updates or emergencies
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Alert Title *</Label>
              <Input
                id="title"
                value={alertForm.title}
                onChange={(e) => setAlertForm({...alertForm, title: e.target.value})}
                placeholder="e.g., System Maintenance Tonight"
                data-testid="alert-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message *</Label>
              <Textarea
                id="message"
                value={alertForm.message}
                onChange={(e) => setAlertForm({...alertForm, message: e.target.value})}
                placeholder="Enter the alert message..."
                rows={4}
                data-testid="alert-message-input"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Urgency Level</Label>
                <Select 
                  value={alertForm.urgency} 
                  onValueChange={(v) => setAlertForm({...alertForm, urgency: v})}
                >
                  <SelectTrigger data-testid="alert-urgency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">
                      <div className="flex items-center gap-2">
                        <Info className="w-4 h-4 text-blue-600" />
                        Info
                      </div>
                    </SelectItem>
                    <SelectItem value="warning">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                        Warning
                      </div>
                    </SelectItem>
                    <SelectItem value="emergency">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-600" />
                        Emergency
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Target Audience</Label>
                <Select 
                  value={alertForm.target} 
                  onValueChange={(v) => setAlertForm({...alertForm, target: v})}
                >
                  <SelectTrigger data-testid="alert-target-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_responders">All Responders</SelectItem>
                    <SelectItem value="active_only">Active (Online) Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {alertForm.urgency === "emergency" && (
              <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="font-medium text-red-800">Include SMS</p>
                    <p className="text-sm text-red-600">Send SMS to responders with phone numbers</p>
                  </div>
                </div>
                <Switch
                  checked={alertForm.include_sms}
                  onCheckedChange={(v) => setAlertForm({...alertForm, include_sms: v})}
                  data-testid="include-sms-switch"
                />
              </div>
            )}

            <Button 
              className="w-full"
              onClick={() => setShowConfirmDialog(true)}
              disabled={!alertForm.title.trim() || !alertForm.message.trim()}
              data-testid="send-alert-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Alert
            </Button>
          </CardContent>
        </Card>

        {/* Alert History */}
        <Card className="card-valor">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Alert History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : alertHistory.length > 0 ? (
              <div className="space-y-4">
                {alertHistory.map((alert) => (
                  <div 
                    key={alert.alert_id} 
                    className="p-4 bg-muted/50 rounded-lg"
                    data-testid={`alert-history-${alert.alert_id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getUrgencyIcon(alert.urgency)}
                        <h4 className="font-semibold">{alert.title}</h4>
                      </div>
                      {getUrgencyBadge(alert.urgency)}
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{alert.message}</p>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {alert.recipients_count} recipients
                      </span>
                      <span>Push: {alert.push_sent_count}</span>
                      {alert.sms_sent_count > 0 && <span>SMS: {alert.sms_sent_count}</span>}
                      <span>By: {alert.sent_by_name}</span>
                      <span>{formatDate(alert.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">No alerts sent yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {getUrgencyIcon(alertForm.urgency)}
              Confirm Alert
            </DialogTitle>
            <DialogDescription>
              Review your alert before sending to all responders
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold">{alertForm.title}</h4>
                {getUrgencyBadge(alertForm.urgency)}
              </div>
              <p className="text-sm text-muted-foreground">{alertForm.message}</p>
            </div>
            
            <div className="flex flex-wrap gap-2 text-sm">
              <Badge variant="outline">
                <Users className="w-3 h-3 mr-1" />
                {alertForm.target === "all_responders" ? "All Responders" : "Active Only"}
              </Badge>
              <Badge variant="outline">Push Notifications</Badge>
              {alertForm.include_sms && <Badge variant="outline">+ SMS</Badge>}
            </div>

            {alertForm.urgency === "emergency" && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                  <strong>Warning:</strong> This is an emergency alert. All responders will receive 
                  immediate notifications{alertForm.include_sms ? " including SMS" : ""}.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendAlert}
              disabled={isSending}
              className={alertForm.urgency === "emergency" ? "bg-red-600 hover:bg-red-700" : ""}
              data-testid="confirm-send-alert-btn"
            >
              {isSending ? "Sending..." : "Send Alert"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBulkAlerts;
